import React from 'react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { ENTITY_TYPES } from '../../lib/kyc';

const Field = ({ label, required, children }) => (
  <div className="space-y-2">
    <Label className="text-xs uppercase tracking-wider">{label}{required && ' *'}</Label>
    {children}
  </div>
);

const NEEDS_DIRECTORS = ['partnership', 'llp', 'private_limited', 'trust', 'society'];
const NEEDS_GST = ['proprietorship', 'partnership', 'llp', 'private_limited', 'trust', 'society'];

export const BusinessInfoStep = ({ form, onChange, onDirectorChange, onAddDirector }) => (
  <>
    <Field label="Business / Legal Name" required>
      <Input value={form.business_name} onChange={e => onChange('business_name', e.target.value)} placeholder="Your Business Name" className="rounded-sm" data-testid="business-name-input" />
    </Field>
    <Field label="Entity Type" required>
      <Select value={form.entity_type} onValueChange={v => onChange('entity_type', v)}>
        <SelectTrigger className="rounded-sm" data-testid="entity-type-select"><SelectValue placeholder="Select entity type" /></SelectTrigger>
        <SelectContent>{ENTITY_TYPES.map(t => <SelectItem key={t.value} value={t.value} data-testid={`entity-option-${t.value}`}>{t.label}</SelectItem>)}</SelectContent>
      </Select>
      <p className="text-xs text-muted-foreground">Required documents are determined by your entity type.</p>
    </Field>
    <div className="grid sm:grid-cols-2 gap-4">
      <Field label={form.entity_type === 'individual' ? 'PAN Number' : 'Business PAN'} required>
        <Input value={form.pan_number} onChange={e => onChange('pan_number', e.target.value.toUpperCase())} placeholder="ABCDE1234F" maxLength={10} className="rounded-sm font-mono" data-testid="pan-number-input" />
      </Field>
      {NEEDS_GST.includes(form.entity_type) && (
        <Field label="GST Number">
          <Input value={form.gst_number} onChange={e => onChange('gst_number', e.target.value.toUpperCase())} placeholder="22AAAAA0000A1Z5" className="rounded-sm font-mono" data-testid="gst-number-input" />
        </Field>
      )}
    </div>
    <Field label="Business Description" required>
      <Textarea value={form.business_description} onChange={e => onChange('business_description', e.target.value)} placeholder="Describe your business, products/services offered..." rows={3} className="rounded-sm" data-testid="business-description-input" />
    </Field>
    <div className="grid sm:grid-cols-2 gap-4">
      <Field label="Website URL">
        <Input value={form.website_url} onChange={e => onChange('website_url', e.target.value)} placeholder="https://yourwebsite.com" className="rounded-sm" data-testid="website-url-input" />
      </Field>
      <Field label="Expected Monthly Payout Volume (₹)">
        <Input type="number" value={form.expected_monthly_volume} onChange={e => onChange('expected_monthly_volume', e.target.value)} placeholder="500000" className="rounded-sm font-mono" data-testid="monthly-volume-input" />
      </Field>
    </div>
    {NEEDS_DIRECTORS.includes(form.entity_type) && (
      <div className="space-y-3 pt-2">
        <div className="flex items-center justify-between">
          <Label className="text-xs uppercase tracking-wider">Directors / Partners / Trustees</Label>
          <Button type="button" variant="outline" size="sm" onClick={onAddDirector} className="rounded-sm" data-testid="add-director-btn">Add</Button>
        </div>
        {form.directors.map((d, i) => (
          <div key={i} className="grid grid-cols-3 gap-3 p-3 border border-border/50 rounded-sm">
            <Input value={d.name} onChange={e => onDirectorChange(i, 'name', e.target.value)} placeholder="Full Name" className="rounded-sm" data-testid={`director-name-${i}`} />
            <Input value={d.pan_number} onChange={e => onDirectorChange(i, 'pan_number', e.target.value.toUpperCase())} placeholder="PAN" maxLength={10} className="rounded-sm font-mono" data-testid={`director-pan-${i}`} />
            <Input value={d.din_number} onChange={e => onDirectorChange(i, 'din_number', e.target.value)} placeholder="DIN (optional)" className="rounded-sm font-mono" data-testid={`director-din-${i}`} />
          </div>
        ))}
        <Textarea value={form.ubo_declaration} onChange={e => onChange('ubo_declaration', e.target.value)} placeholder="UBO declaration — owners holding >25% equity (optional)" rows={2} className="rounded-sm" data-testid="ubo-declaration-input" />
      </div>
    )}
  </>
);

export const BankDetailsStep = ({ bank, onChange }) => (
  <>
    <Field label="Account Holder Name" required>
      <Input value={bank.account_holder_name} onChange={e => onChange('account_holder_name', e.target.value)} placeholder="As per bank records" className="rounded-sm" data-testid="account-holder-input" />
    </Field>
    <Field label="Account Number" required>
      <Input value={bank.account_number} onChange={e => onChange('account_number', e.target.value)} placeholder="Your bank account number" className="rounded-sm font-mono" data-testid="account-number-input" />
    </Field>
    <div className="grid sm:grid-cols-2 gap-4">
      <Field label="IFSC Code" required>
        <Input value={bank.ifsc_code} onChange={e => onChange('ifsc_code', e.target.value.toUpperCase())} placeholder="SBIN0001234" className="rounded-sm font-mono" data-testid="ifsc-code-input" />
      </Field>
      <Field label="Bank Name" required>
        <Input value={bank.bank_name} onChange={e => onChange('bank_name', e.target.value)} placeholder="State Bank of India" className="rounded-sm" data-testid="bank-name-input" />
      </Field>
    </div>
  </>
);

const DECLARATIONS = [
  ['agreement_accepted', 'Merchant Agreement', 'I have read and agree to the Merchant Services Agreement, Terms of Service and Privacy Policy.', 'agreement-checkbox'],
  ['aml_declaration_accepted', 'AML Declaration', 'I confirm that our business complies with Anti-Money Laundering regulations and will maintain adequate KYC records.', 'aml-checkbox'],
  ['risk_disclosure_accepted', 'Risk Disclosure Acceptance', 'I understand and accept the fee, settlement and risk-control policies applicable to my business category.', 'risk-checkbox'],
];

export const DeclarationsStep = ({ values, onChange }) => (
  <div className="space-y-3">
    {DECLARATIONS.map(([key, title, text, tid]) => (
      <label key={key} htmlFor={key} className="flex items-start gap-3 p-4 border border-border/50 rounded-sm cursor-pointer hover:bg-accent/40 transition-colors">
        <Checkbox id={key} checked={!!values[key]} onCheckedChange={c => onChange(key, !!c)} data-testid={tid} className="mt-0.5" />
        <div className="space-y-1">
          <p className="font-medium text-sm">{title} *</p>
          <p className="text-xs text-muted-foreground">{text}</p>
        </div>
      </label>
    ))}
  </div>
);
